import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import ProductImage from 'components/Product/ProductImage/ProductImage';
import Empty from 'constants/empty';
import Modal from 'components/Modal/Modal';
import {
    space, mediaQueries, colors, borders, fontSizes
} from 'style/config';
import {
    Button, Box, Text, Flex, Grid
} from 'components/ui';
import SwatchesDisplay from 'components/ProductPage/Swatches/SwatchesDisplay';
import {
    MAX_ONLY_A_FEW_LEFT_IN_LOVES_LIST_FLYOUT,
    MAX_FLYOUT_RECENT_ITEMS,
    MIN_ITEMS_PER_PAGE,
    REPLACING_ITEMS_ERROR_CODES,
    ERROR_CODES
} from 'constants/sharableList';

function EditMyListProductModal(props) {
    const {
        isOpen,
        localization,
        swatchGroupView,
        swatchGroupsAndFilters,
        skuToEdit,
        variationType,
        showEditMyListProductModal,
        setSkuLoveData,
        productHasVariants,
        saveReplaceItem,
        isSizeVariation
    } = props;

    const [selectedSku, setSelectedSku] = useState(skuToEdit || null);
    const [hasSelectedNewSku, setHasSelectedNewSku] = useState(false);

    const handleClick = ({ refreshListsData } = {}) => {
        const { getAllLists, setLoveListUpdated, getLimitedLoveListItems, getFlatLoveListSkusWithDetails } = props;
        showEditMyListProductModal({ isOpen: false });

        if (refreshListsData) {
            getAllLists({
                callback: () => {
                    setLoveListUpdated(false);
                },
                force: true,
                options: {
                    itemsPerPage: MIN_ITEMS_PER_PAGE,
                    skipProductDetails: false
                }
            });

            getLimitedLoveListItems({
                refreshCache: true,
                options: {
                    itemsPerPage: MAX_ONLY_A_FEW_LEFT_IN_LOVES_LIST_FLYOUT
                }
            });
            getFlatLoveListSkusWithDetails(null, true, {
                itemsPerPage: MAX_FLYOUT_RECENT_ITEMS,
                currentPage: 1
            });
        }

        setSkuLoveData({
            loveSource: null,
            skuId: null,
            productId: null
        });
    };

    const handleSelectSkuToEditClick = sku => {
        const updatedSku = {
            ...skuToEdit,
            currentSku: sku
        };

        setSelectedSku(updatedSku);
        setHasSelectedNewSku(sku.skuId !== skuToEdit?.currentSku?.skuId);
    };

    const handleReplaceItem = () => {
        const { updateReduxLoveList, showInfoModal } = props;

        if (!productHasVariants || !hasSelectedNewSku) {
            handleClick({ refreshListsData: false });
        } else {
            saveReplaceItem({
                skuToSave: selectedSku?.currentSku,
                callback: () => {
                    handleClick({ refreshListsData: true });
                    updateReduxLoveList();
                },
                cbCleanup: err => {
                    const error = {
                        title: REPLACING_ITEMS_ERROR_CODES[err?.errorCode]
                            ? localization[REPLACING_ITEMS_ERROR_CODES[err?.errorCode]]
                            : localization.edit,
                        message: err.errorMessages?.[0] || localization.genericErrorMessage,
                        buttonText: localization.gotIt
                    };

                    if (err?.errorCode !== ERROR_CODES.DUPLICATE_ITEM) {
                        handleClick({ refreshListsData: false });
                    }

                    showInfoModal(error);
                }
            });
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            width={0}
            isDrawer={true}
            hasBodyScroll={productHasVariants}
            onDismiss={handleClick}
        >
            <Modal.Header>
                <Modal.Title>{localization.edit}</Modal.Title>
            </Modal.Header>
            <Modal.Body
                lineHeight='tight'
                paddingBottom={4}
                css={styles.mainListWrapper}
            >
                {skuToEdit && (
                    <>
                        <Grid
                            columns='auto 1fr'
                            alignItems='flex-start'
                            gap={2}
                            css={styles.productHeader}
                        >
                            <ProductImage
                                id={skuToEdit.currentSku?.skuId}
                                skuImages={skuToEdit.currentSku?.skuImages}
                                size={48}
                                disableLazyLoad={true}
                            />
                            <Box fontSize='base'>
                                <Text
                                    display='block'
                                    fontWeight='bold'
                                    numberOfLines={1}
                                    children={skuToEdit.currentSku?.brandName}
                                    is='h4'
                                />
                                <Text
                                    display='block'
                                    children={skuToEdit.currentSku?.productName}
                                />
                            </Box>
                        </Grid>
                        {productHasVariants ? (
                            <>
                                <Text
                                    is='h3'
                                    css={styles.variantTitle}
                                >
                                    {localization[variationType]}
                                </Text>
                                <SwatchesDisplay
                                    isSmallView={true}
                                    isSkuReady={true}
                                    selectedSwatchView={swatchGroupView}
                                    product={selectedSku}
                                    {...swatchGroupsAndFilters}
                                    currentProduct={selectedSku}
                                    hideSwatchSelectorType={true}
                                    hideSizeAndDescription={true}
                                    hideListBorder={true}
                                    noMaxHeight={true}
                                    hideMarketingFlags={true}
                                    hideInventoryStatus={true}
                                    hideSaleFlag={true}
                                    skipAnalytics={true}
                                    handleSelectSkuToEditClick={handleSelectSkuToEditClick}
                                    customCss={{
                                        ...styles.customCss,
                                        swatchItem: {
                                            ...styles.customCss.swatchItem,
                                            ...(isSizeVariation && styles.customCss.swatchItemSizeVariation)
                                        },
                                        swatchGroup: {
                                            ...styles.customCss.swatchGroup,
                                            ...(isSizeVariation && styles.customCss.swatchGroupSizeVariation)
                                        }
                                    }}
                                />
                            </>
                        ) : (
                            <Text is='p'>{localization.noVariants}</Text>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer paddingX={[3, 3]}>
                <Flex css={styles.ctaContainer}>
                    {productHasVariants && (
                        <Button
                            onClick={handleClick}
                            variant='secondary'
                            css={styles.secondaryBtn}
                        >
                            {localization.cancel}
                        </Button>
                    )}
                    <Button
                        onClick={handleReplaceItem}
                        variant='primary'
                        css={styles.primaryBtn}
                    >
                        {productHasVariants ? localization.save : localization.gotIt}
                    </Button>
                </Flex>
            </Modal.Footer>
        </Modal>
    );
}

const styles = {
    mainListWrapper: {
        [mediaQueries.md]: {
            maxHeight: '55vmax'
        },
        '> :last-child': {
            borderBottom: 'none',
            paddingBottom: 0
        }
    },
    myListItem: {
        ':first-child': {
            paddingTop: 0
        }
    },
    ctaContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: 16,
        marginTop: 2
    },
    primaryBtn: {
        width: '100%'
    },
    secondaryBtn: {
        width: '100%'
    },
    variantTitle: {
        fontWeight: 'bold',
        marginLeft: space[1],
        [mediaQueries.xsMax]: {
            marginLeft: 0
        }
    },
    skuDescription: {
        display: '-webkit-box',
        '-webkit-box-orient': 'vertical',
        '-webkit-line-clamp': '2',
        overflow: 'hidden'
    },
    productHeader: {
        borderBottom: `1px solid ${colors.lightGray}`,
        marginTop: `-${space[2]}px`,
        paddingBottom: `${space[2]}px`,
        marginBottom: `${space[4]}px`
    },
    customCss: {
        swatchGroup: {
            paddingTop: 0,
            marginTop: 0,
            paddingBottom: 0
        },
        swatchGroupSizeVariation: {
            'div:last-child': {
                marginBottom: 0
            }
        },
        groupName: {
            paddingLeft: space[1],
            [mediaQueries.xsMax]: {
                marginLeft: space[4]
            }
        },
        swatchItem: {
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: space[1],
            fontSize: fontSizes['sm'],
            [mediaQueries.xsMax]: {
                paddingLeft: space[4]
            }
        },
        swatchItemSizeVariation: {
            border: `${borders[2]} ${colors.lightGray}`,
            borderRadius: space[2],
            marginBottom: space[2],
            maxWidth: '60%',
            div: { border: 'none' },
            [mediaQueries.xsMax]: {
                marginLeft: space[4]
            },
            activeSwatchItem: {
                border: '2px solid ' + colors.black
            }
        }
    }
};

EditMyListProductModal.propTypes = {
    isOpen: PropTypes.bool,
    skuToEdit: PropTypes.object,
    swatchGroupView: PropTypes.string,
    swatchGroupsAndFilters: PropTypes.object
};

EditMyListProductModal.defaultProps = {
    isOpen: false,
    skuToEdit: Empty.Object,
    swatchGroupView: '',
    swatchGroupsAndFilters: Empty.Object
};

export default wrapFunctionalComponent(EditMyListProductModal, 'EditMyListProductModal', true);
